import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const FUNNEL = [
  ["new", "Applied"], ["screening", "Screening"], ["shortlisted", "Shortlisted"],
  ["interview", "Interview"], ["offer", "Offer"], ["hired", "Hired"], ["rejected", "Rejected"],
];

export default function JobReport({ jobId, onClose }) {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);

  const load = useCallback(() => {
    if (!jobId) return;
    api.get(`/employer/jobs/${jobId}/report`, { params: { days } })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [jobId, days]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const r = await api.get(`/employer/jobs/${jobId}/report`, { params: { days, format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `job-report-${days}d.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const kpis = report ? [
    ["Views", report.views], ["Unique visitors", report.unique_visitors],
    ["Apply starts", report.apply_starts], ["Applications", report.applications],
    ["Conversion", `${report.conversion}%`], ["Saves", report.saves],
  ] : [];

  return (
    <Dialog open={!!jobId} onOpenChange={onClose}>
      <DialogContent className="bg-white sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="job-report">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold">
            Job report{report ? `: ${report.job.title}` : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`report-days-${d}`}
              className={`h-8 px-3 text-xs font-semibold rounded-md border transition-colors duration-200 ${days === d ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-600"}`}>
              {d} days
            </button>
          ))}
          <button onClick={downloadCsv} data-testid="report-csv"
            className="h-8 px-3 text-xs font-semibold rounded-md border border-zinc-300 text-zinc-700 hover:border-zinc-500 inline-flex items-center gap-1.5 ml-auto">
            <Download className="h-3.5 w-3.5" /> Download CSV
          </button>
        </div>
        {!report ? (
          <div className="py-10 space-y-3">{[...Array(3)].map((_, i) => <div key={`report-skel-${i}`} className="h-16 bg-zinc-100 rounded-md animate-pulse" />)}</div>
        ) : (
          <div className="space-y-8 mt-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="report-kpis">
              {kpis.map(([label, val]) => (
                <div key={label} className="border border-zinc-200 rounded-md p-4" data-testid={`report-kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
                  <p className="font-display text-2xl font-black text-zinc-950">{val}</p>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mt-1">{label}</p>
                </div>
              ))}
            </div>

            <section data-testid="report-funnel">
              <h3 className="text-sm font-bold text-zinc-950 mb-3">Hiring funnel</h3>
              <div className="space-y-1.5">
                {FUNNEL.map(([key, label]) => {
                  const count = report.funnel[key] || 0;
                  const max = Math.max(1, report.applications);
                  return (
                    <div key={key} className="flex items-center gap-3" data-testid={`report-funnel-${key}`}>
                      <span className="text-xs text-zinc-600 w-24 shrink-0">{label}</span>
                      <div className="flex-1 h-5 bg-zinc-100 rounded-sm overflow-hidden">
                        <div className={`h-full rounded-sm ${key === "hired" ? "bg-green-500" : key === "rejected" ? "bg-red-400" : "bg-zinc-900"}`}
                          style={{ width: `${(count / max) * 100}%` }} />
                      </div>
                      <span className="text-xs font-bold text-zinc-950 w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </section>

            <section data-testid="report-sources">
              <h3 className="text-sm font-bold text-zinc-950 mb-3">Where candidates came from</h3>
              {report.sources.length === 0 ? (
                <p className="text-xs text-zinc-500">No traffic in this period yet.</p>
              ) : (
                <div className="border border-zinc-200 rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                        <th className="px-3 py-2 text-xs font-semibold text-zinc-600">Source</th>
                        <th className="px-3 py-2 text-xs font-semibold text-zinc-600">Views</th>
                        <th className="px-3 py-2 text-xs font-semibold text-zinc-600">Applications</th>
                        <th className="px-3 py-2 text-xs font-semibold text-zinc-600">Conversion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.sources.map((row) => (
                        <tr key={row.source} className="border-b border-zinc-100 last:border-0" data-testid={`report-source-${row.source.replace(/\W+/g, "-").toLowerCase()}`}>
                          <td className="px-3 py-2 font-medium text-zinc-900">{row.source}</td>
                          <td className="px-3 py-2 text-zinc-600">{row.views}</td>
                          <td className="px-3 py-2 text-zinc-600">{row.applications}</td>
                          <td className="px-3 py-2 text-zinc-600">{row.conversion}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <div className="grid sm:grid-cols-2 gap-4">
              <section data-testid="report-devices">
                <h3 className="text-sm font-bold text-zinc-950 mb-2">Devices</h3>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(report.devices).map(([d, n]) => (
                    <span key={d} className="text-xs bg-zinc-100 text-zinc-700 px-3 py-1.5 rounded-full font-medium">{d}: {n}</span>
                  ))}
                  {Object.keys(report.devices).length === 0 && <p className="text-xs text-zinc-500">No data yet.</p>}
                </div>
              </section>
              <section data-testid="report-timing">
                <h3 className="text-sm font-bold text-zinc-950 mb-2">Speed</h3>
                <p className="text-xs text-zinc-600">Time to shortlist: <span className="font-bold text-zinc-950">{report.avg_hours_to_shortlist != null ? `${report.avg_hours_to_shortlist}h` : "not enough data"}</span></p>
                <p className="text-xs text-zinc-600 mt-1">Time to hire: <span className="font-bold text-zinc-950">{report.avg_hours_to_hire != null ? `${report.avg_hours_to_hire}h` : "not enough data"}</span></p>
              </section>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
